/**
 * Redeem Code API Client
 *
 * Wraps the customer-facing /api/redeem-codes/validate endpoint. This
 * is a pre-flight check — actual redemption happens atomically inside
 * the session-start transaction on the server. The UI uses this to
 * give the customer immediate feedback when they hit "Apply" so they
 * can correct a typo before committing to the charging flow.
 *
 * The customer app runs anonymously (no logged-in userId), so the
 * server accepts the code alone. Possession of the code is treated
 * as proof — analogous to a promo / gift code.
 */

import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL;

function correlationId() {
  return `redeem-${Date.now()}-${Math.random().toString(36).substring(7)}`;
}

/**
 * Validate a redeem code.
 *
 * @param {object} params
 * @param {string} params.code  The redeem code (e.g. "GC-8K42P").
 * @returns {Promise<object>} Either
 *   { valid: true,  code, amount, currency, expiresAt }
 *   { valid: false, message }
 */
export async function validateRedeemCode({ code }) {
  const cid = correlationId();
  try {
    const payload = { code: String(code || '').trim().toUpperCase() };
    const response = await axios.post(
      `${API_BASE}/redeem-codes/validate`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': cid,
        },
      },
    );
    return response.data;
  } catch (err) {
    // The server always responds with { valid: false, message } for
    // known failures, so anything landing here is a network / 5xx
    // that we should surface as a generic error.
    const message =
      err?.response?.data?.message ||
      'Could not validate code. Please try again.';
    return { valid: false, message };
  }
}
